import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Badge={
  badgeKey:string;
  category:string;
  teacherName:string;
  studentName:string;
  description:string;
  recommendedActivityTypes:string[];
  minScore:number;
  imageUri?:string|null;
  credentialDefinitionId:string;
  achievementDefinitionId:string;
  evaluationDefinitionId:string;
  evidenceReadyCount:number;
  issuedCount:number;
};

type RosterRow={
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  evidenceId?:string|null;
  evidenceTitle?:string|null;
  evidenceType?:string|null;
  evidenceObservedAt?:string|null;
  activityType?:string|null;
  activityTitle?:string|null;
  awardStatus:string;
  credentialId?:string|null;
  requestId?:string|null;
  achievementId?:string|null;
};

type Filter="READY"|"ALL"|"REQUESTED"|"ISSUED"|"NO_EVIDENCE";

function activityLabel(type?:string|null){
  const map:Record<string,string>={
    ASSESSMENT:"검사",
    REFLECTION:"성찰",
    COUNSELING:"상담",
    LESSON:"수업",
    EXPERIENCE:"체험",
    INTERVIEW:"인터뷰",
    PROJECT:"프로젝트",
    PRESENTATION:"발표"
  };
  return type ? map[type] || type : "-";
}

function statusLabel(value:string){
  if(value==="READY") return "발급 준비";
  if(value==="REQUESTED") return "발급 요청";
  if(value==="APPROVED") return "승인";
  if(value==="PROCESSING") return "발급 중";
  if(value==="ISSUED") return "발급 완료";
  if(value==="CONFIRMED") return "성취 확인";
  if(value==="CANDIDATE") return "성취 후보";
  if(value==="NO_EVIDENCE") return "증빙 없음";
  return value;
}

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleDateString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit"
  });
}

export function CenterBadgesPage({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const initialParams=new URLSearchParams(window.location.search);
  const [badges,setBadges]=useState<Badge[]>([]);
  const [selectedKey,setSelectedKey]=useState("");
  const [roster,setRoster]=useState<RosterRow[]>([]);
  const [category,setCategory]=useState("전체");
  const [filter,setFilter]=useState<Filter>("READY");
  const [selectedLearners,setSelectedLearners]=useState<string[]>([]);
  const [score,setScore]=useState("80");
  const [periodKey,setPeriodKey]=useState(String(new Date().getFullYear()));
  const [studentQuery,setStudentQuery]=useState("");
  const [gradeFilter,setGradeFilter]=useState(initialParams.get("grade") || "전체");
  const [classFilter,setClassFilter]=useState(initialParams.get("class") || "전체");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_badge_catalog",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=(data || []) as Badge[];
      setBadges(next);
      setSelectedKey((current)=>
        next.some((item)=>item.badgeKey===current)
          ? current
          : next.find((item)=>Number(item.evidenceReadyCount)>0)?.badgeKey || next[0]?.badgeKey || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const selectedBadge=useMemo(
    ()=>badges.find((item)=>item.badgeKey===selectedKey) || null,
    [badges,selectedKey]
  );

  useEffect(()=>{
    if(!selectedKey){
      setRoster([]);
      return;
    }
    let active=true;
    setError("");
    client.rpc("center_badge_roster",{
      p_school_id:schoolId,
      p_badge_key:selectedKey
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const rows=(data || []) as RosterRow[];
      setRoster(rows);
      setSelectedLearners((current)=>
        current.filter((learnerId)=>
          rows.some((item)=>item.learnerId===learnerId && item.awardStatus==="READY")
        )
      );
    });
    return()=>{active=false;};
  },[client,schoolId,selectedKey,refresh]);

  useEffect(()=>{
    if(selectedBadge){
      setScore(String(Math.max(Number(selectedBadge.minScore),80)));
    }
  },[selectedBadge?.badgeKey]);

  const categories=useMemo(
    ()=>["전체",...Array.from(new Set(badges.map((item)=>item.category)))],
    [badges]
  );

  const filteredBadges=useMemo(
    ()=>badges.filter((item)=>category==="전체" || item.category===category),
    [badges,category]
  );

  const gradeOptions=useMemo(
    ()=>["전체",...Array.from(new Set(roster.map((item)=>item.gradeLevel).filter(Boolean) as string[])).sort((a,b)=>Number(a)-Number(b))],
    [roster]
  );

  const classOptions=useMemo(
    ()=>["전체",...Array.from(new Set(roster.map((item)=>item.classLabel).filter(Boolean) as string[])).sort()],
    [roster]
  );

  const filteredRoster=useMemo(()=>{
    const q=studentQuery.trim().toLowerCase();
    return roster.filter((item)=>{
      if(filter==="REQUESTED" && !["REQUESTED","APPROVED","PROCESSING","CONFIRMED","CANDIDATE"].includes(item.awardStatus))return false;
      if(filter!=="ALL" && filter!=="REQUESTED" && item.awardStatus!==filter)return false;
      if(gradeFilter!=="전체" && item.gradeLevel!==gradeFilter)return false;
      if(classFilter!=="전체" && item.classLabel!==classFilter)return false;
      if(q && ![item.displayName,item.gradeLevel,item.classLabel,item.activityTitle,item.evidenceTitle].filter(Boolean).join(" ").toLowerCase().includes(q))return false;
      return true;
    });
  },[roster,filter,studentQuery,gradeFilter,classFilter]);

  const readyRows=filteredRoster.filter((item)=>item.awardStatus==="READY" && item.evidenceId);
  const allReadySelected=
    readyRows.length>0 &&
    readyRows.every((item)=>selectedLearners.includes(item.learnerId));

  function toggleLearner(id:string){
    setSelectedLearners((current)=>
      current.includes(id)
        ? current.filter((item)=>item!==id)
        : [...current,id]
    );
  }

  function toggleAllReady(){
    if(allReadySelected){
      setSelectedLearners([]);
      return;
    }
    setSelectedLearners(readyRows.map((item)=>item.learnerId));
  }

  async function awardSelected(){
    if(!selectedBadge || selectedLearners.length===0)return;
    const numericScore=Number(score);

    if(!Number.isFinite(numericScore) || numericScore<selectedBadge.minScore || numericScore>100){
      setError("배지 기준점수 이상 100점 이하로 입력해 주세요.");
      return;
    }

    setBusy("award");
    setError("");
    setMessage("");

    try{
      const rows=selectedLearners.flatMap((learnerId)=>{
        const row=roster.find((item)=>
          item.learnerId===learnerId &&
          item.awardStatus==="READY" &&
          item.evidenceId
        );
        return row?.evidenceId ? [{learnerId,evidenceId:row.evidenceId}] : [];
      });

      const {data,error}=await client.rpc("center_award_career_badge_batch",{
        p_school_id:schoolId,
        p_badge_key:selectedBadge.badgeKey,
        p_rows:rows,
        p_score:numericScore,
        p_period_key:periodKey.trim() || "LIFETIME"
      });
      if(error)throw new Error(error.message);

      const result=data as {
        jobId:string;
        requestedCount:number;
        succeededCount:number;
        failedCount:number;
        status:string;
      };

      setMessage(
        `배지 일괄작업 완료 · 요청 ${result.requestedCount}명 / 성공 ${result.succeededCount}명`+
        (result.failedCount ? ` / 실패 ${result.failedCount}명 · 일괄작업 이력을 확인하세요.` : "")
      );
      setSelectedLearners([]);
      setRefresh((value)=>value+1);
    }catch(err){
      setError(err instanceof Error ? err.message : "배지 일괄작업 중 오류가 발생했습니다.");
    }finally{
      setBusy("");
    }
  }

  return (
    <section className="teacher-badge-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Life Stamp</p>
          <h2>배지를 고르고, 준비된 학생에게 바로 주세요</h2>
          <p className="route-description">
            배지를 선택하면 조건을 충족한 학생이 먼저 보입니다. 학생을 고르고 한 번에 부여하세요.
          </p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="badge-category-tabs">
        {categories.map((item)=>(
          <button
            key={item}
            className={category===item ? "filter-chip active" : "filter-chip"}
            onClick={()=>setCategory(item)}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="teacher-badge-grid">
        {filteredBadges.map((badge)=>(
          <button
            key={badge.badgeKey}
            className={selectedKey===badge.badgeKey ? "teacher-badge-card active" : "teacher-badge-card"}
            onClick={()=>setSelectedKey(badge.badgeKey)}
          >
            <img src={badge.imageUri || ""} alt={badge.studentName} />
            <div>
              <span>{badge.category}</span>
              <strong>{badge.teacherName}</strong>
              <p>{badge.description}</p>
            </div>
            <dl>
              <div><dt>준비 학생</dt><dd>{badge.evidenceReadyCount}</dd></div>
              <div><dt>발급 완료</dt><dd>{badge.issuedCount}</dd></div>
            </dl>
          </button>
        ))}
      </div>

      {selectedBadge && (
        <section className="badge-roster-section">
          <header className="badge-roster-head">
            <div className="badge-roster-title">
              <img src={selectedBadge.imageUri || ""} alt="" />
              <div>
                <p className="eyebrow">{selectedBadge.category}</p>
                <h2>{selectedBadge.teacherName}</h2>
                <p>{selectedBadge.description}</p>
              </div>
            </div>
            <div className="badge-rule-card">
              <span>발급 기준</span>
              <strong>{selectedBadge.minScore}점 이상 + 검증 증빙 1건</strong>
              <small>
                추천 활동 · {selectedBadge.recommendedActivityTypes.map(activityLabel).join(" · ")}
              </small>
            </div>
          </header>

          <div className="badge-award-toolbar">
            <div className="teacher-filter-row">
              <input
                className="teacher-search"
                value={studentQuery}
                onChange={(event)=>setStudentQuery(event.target.value)}
                placeholder="학생 이름 검색"
              />
              <select value={gradeFilter} onChange={(event)=>setGradeFilter(event.target.value)}>
                {gradeOptions.map((item)=><option key={item} value={item}>{item==="전체" ? "전체 학년" : item+"학년"}</option>)}
              </select>
              <select value={classFilter} onChange={(event)=>setClassFilter(event.target.value)}>
                {classOptions.map((item)=><option key={item} value={item}>{item==="전체" ? "전체 반" : item+"반"}</option>)}
              </select>
            </div>

            <div className="badge-filter-row">
              {([
                ["READY","발급 준비"],
                ["REQUESTED","진행 중"],
                ["ISSUED","발급 완료"],
                ["NO_EVIDENCE","증빙 없음"],
                ["ALL","전체"]
              ] as Array<[Filter,string]>).map(([value,label])=>(
                <button
                  key={value}
                  className={filter===value ? "filter-chip active" : "filter-chip"}
                  onClick={()=>setFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="badge-award-controls">
              <label>
                평가점수
                <input
                  type="number"
                  min={selectedBadge.minScore}
                  max="100"
                  value={score}
                  onChange={(event)=>setScore(event.target.value)}
                />
              </label>
              <label>
                기간
                <input
                  value={periodKey}
                  onChange={(event)=>setPeriodKey(event.target.value)}
                  placeholder="2026"
                />
              </label>
              <button
                className="primary"
                disabled={busy!=="" || selectedLearners.length===0}
                onClick={awardSelected}
              >
                {busy==="award"
                  ? "배지를 주는 중…"
                  : selectedLearners.length+"명에게 배지 주기"}
              </button>
            </div>
          </div>

          <div className="badge-roster-table">
            <div className="badge-roster-header">
              <label>
                <input
                  type="checkbox"
                  checked={allReadySelected}
                  onChange={toggleAllReady}
                />
                <span>학생</span>
              </label>
              <span>근거 활동</span>
              <span>검증 증빙</span>
              <span>상태</span>
            </div>

            {filteredRoster.map((row)=>(
              <div className="badge-roster-row" key={row.learnerId}>
                <label>
                  <input
                    type="checkbox"
                    disabled={row.awardStatus!=="READY" || !row.evidenceId}
                    checked={selectedLearners.includes(row.learnerId)}
                    onChange={()=>toggleLearner(row.learnerId)}
                  />
                  <span>
                    <strong>{row.displayName}</strong>
                    <small>
                      {row.gradeLevel ? row.gradeLevel+"학년" : "학년 미정"}
                      {row.classLabel ? " · "+row.classLabel+"반" : ""}
                    </small>
                  </span>
                </label>

                <div>
                  <strong>{row.activityTitle || activityLabel(row.activityType)}</strong>
                  <small>{activityLabel(row.activityType)}</small>
                </div>

                <div>
                  <strong>{row.evidenceTitle || "적합한 검증 증빙 없음"}</strong>
                  <small>{row.evidenceId ? formatDate(row.evidenceObservedAt) : "-"}</small>
                </div>

                <span className={
                  row.awardStatus==="ISSUED"
                    ? "status-pill good"
                    : row.awardStatus==="READY"
                      ? "status-pill ready"
                      : "status-pill"
                }>
                  {statusLabel(row.awardStatus)}
                </span>
              </div>
            ))}

            {!filteredRoster.length && (
              <p className="case-empty">해당 상태의 학생이 없습니다.</p>
            )}
          </div>

          <p className="badge-teacher-note">
            교사는 학생의 활동과 결과를 확인해 배지를 선택합니다.
            발급·서명 같은 기술 처리는 시스템이 뒤에서 자동으로 처리합니다.
          </p>
        </section>
      )}
    </section>
  );
}
